import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  DataGridColumn,
  DataTableSegmentedFilter,
  DataTableTagClick,
  parseColumnMultiFilter,
} from '../data-table/data-table.types';
import { UiDataTableComponent } from '../data-table/data-table.component';
import {
  UiFileItem,
  UiFileKind,
} from '../../molecules/file-upload/file-upload.types';
import { UiFileUploadComponent } from '../../molecules/file-upload/file-upload.component';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiFormSectionComponent } from '../../molecules/form-section/form-section.component';
import { UiTextareaComponent } from '../../atoms/textarea/textarea.component';
import { draftsFromInvoiceFiles } from './invoice-capture.cfdi';
import {
  takeInvoiceCaptureResult,
  UiInvoiceCaptureResult,
  UiInvoiceDraftRow,
} from './invoice-capture.types';

const DRAFT_COLUMNS: DataGridColumn[] = [
  { key: 'archivo', header: 'Archivo XML', type: 'string', width: 190 },
  { key: 'folio', header: 'Folio', type: 'string', width: 100 },
  { key: 'fecha', header: 'Fecha', type: 'string', width: 110 },
  { key: 'emisor', header: 'Emisor', type: 'string', width: 220 },
  { key: 'usoCfdi', header: 'Uso CFDI', type: 'string', width: 100 },
  { key: 'iva', header: 'IVA', type: 'string', width: 120 },
  { key: 'retencionIsr', header: 'Retención ISR', type: 'string', width: 120 },
  { key: 'retencionIva', header: 'Retención IVA', type: 'string', width: 120 },
  { key: 'importe', header: 'Importe', type: 'string', width: 120 },
  {
    key: 'validacion',
    header: 'Validación',
    type: 'string',
    width: 120,
    tagColorMap: {
      'XML válido': 'success',
      'XML inválido': 'danger',
    },
  },
];

const MANUAL_DRAFT_COLUMNS: DataGridColumn[] = [
  { key: 'folio', header: 'Folio', type: 'string', width: 110 },
  { key: 'fecha', header: 'Fecha', type: 'string', width: 120 },
  {
    key: 'estatus',
    header: 'Validación',
    type: 'string',
    width: 130,
    clickable: true,
    tagColorMap: {
      Validado: 'success',
      'Error validación': 'danger',
    },
  },
  { key: 'emisor', header: 'Emisor', type: 'string', width: 220 },
  { key: 'metodoPago', header: 'Método de pago', type: 'string', width: 150 },
  { key: 'formaPago', header: 'Forma de pago', type: 'string', width: 150 },
  { key: 'usoCfdi', header: 'Uso CFDI', type: 'string', width: 110 },
  { key: 'subtotal', header: 'Subtotal', type: 'string', width: 130 },
  { key: 'descuento', header: 'Descuento', type: 'string', width: 130 },
  { key: 'iva', header: 'IVA', type: 'string', width: 120 },
  { key: 'retencionIsr', header: 'Retención ISR', type: 'string', width: 140 },
  { key: 'retencionIva', header: 'Retención IVA', type: 'string', width: 140 },
  { key: 'importe', header: 'Importe', type: 'string', width: 130 },
  { key: 'archivo', header: 'XML', type: 'string', width: 200, clickable: true },
  { key: 'pdf', header: 'PDF', type: 'string', width: 200, clickable: true },
];

const MANUAL_MULTI_FILTER_KEYS = new Set([
  'estatus',
  'emisor',
  'metodoPago',
  'formaPago',
  'usoCfdi',
]);

const MANUAL_VALIDATION_FILTER_OPTIONS = [
  { id: 'estatus-validado', label: 'Validado', value: 'Validado' },
  { id: 'estatus-error', label: 'Error validación', value: 'Error validación' },
];

const MANUAL_TOTAL_COLUMNS = [
  'subtotal',
  'descuento',
  'iva',
  'retencionIsr',
  'retencionIva',
  'importe',
];

function moneyAmount(value: unknown): number {
  const amount = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
}

function distinctDraftOptions(
  rows: readonly UiInvoiceDraftRow[],
  key: 'emisor' | 'metodoPago' | 'formaPago' | 'usoCfdi',
) {
  const values = [...new Set(rows.map(row => row[key]).filter(value => value && value !== '—'))]
    .sort((left, right) => left.localeCompare(right, 'es'));
  return values.map((value, index) => ({
    id: `${key}-${index + 1}`,
    label: value,
    value,
  }));
}

function validationSegmentFilters(): DataTableSegmentedFilter[] {
  return [
    {
      key: 'estatus',
      label: 'Validación',
      value: 'valido',
      options: [
        { value: 'valido', label: 'XML - PDF Validado' },
        { value: 'no-valido', label: 'XML - PDF Error validación' },
      ],
    },
  ];
}

/**
 * Organismo: captura de factura CFDI (XML/PDF + interpretación local).
 * No incluye drawer ni consulta al SAT. La página hidrata archivos/notas y
 * llama `takeResult()` al guardar.
 */
@Component({
  selector: 'ui-invoice-capture',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayModule,
    UiButtonComponent,
    UiDataTableComponent,
    UiFileUploadComponent,
    UiFormSectionComponent,
    UiTextareaComponent,
  ],
  templateUrl: './invoice-capture.component.html',
  styleUrl: './invoice-capture.component.scss',
  host: { class: 'ui-invoice-capture-host' },
})
export class UiInvoiceCaptureComponent {
  readonly files = model<UiFileItem[]>([]);
  readonly notes = model('');
  readonly scrollable = input(false);
  readonly filesTitle = input('Archivos de factura');
  readonly showNotes = input(true);
  /** La tabla se llena al pulsar Validar y después se vacía la carga. */
  readonly manualValidation = input(false);
  /**
   * Tope del subtotal de CFDI validados. `null` no aplica límite.
   * Incluye los renglones validados que ya están en la tabla.
   */
  readonly maxValidatedSubtotal = input<number | null>(null);
  readonly validationTitle = input('Interpretación y validación del XML');
  /** `true` cuando el XML y el PDF del renglón abierto coinciden por nombre. */
  readonly criteriaMatch = output<boolean>();

  protected readonly drafts = signal<UiInvoiceDraftRow[]>([]);
  protected readonly validating = signal(false);
  protected readonly showValidatedTable = signal(false);
  protected readonly infoOpen = signal(false);
  protected readonly panelWidth = signal(600);
  protected readonly criteriaTitle = signal('');
  protected readonly criteriaOrigin = signal<HTMLElement | null>(null);
  protected readonly criteriaPositions = signal<ConnectedPosition[]>([]);
  private criteriaRowId: string | null = null;
  private readonly validatedFiles = signal<UiFileItem[]>([]);
  private readonly draftColumnFilters = signal<Record<string, string>>({});
  protected readonly validationSegments = signal<DataTableSegmentedFilter[]>(
    validationSegmentFilters(),
  );
  protected readonly manualTotalColumns = MANUAL_TOTAL_COLUMNS;
  protected readonly draftTotals = computed(() => {
    const totals: Record<string, number> = {};
    for (const key of MANUAL_TOTAL_COLUMNS) {
      totals[key] = this.visibleDrafts().reduce(
        (sum, row) => sum + moneyAmount(row[key as keyof UiInvoiceDraftRow]),
        0,
      );
    }
    return totals;
  });
  protected readonly fileAccept: UiFileKind[] = ['xml', 'pdf'];
  protected readonly tableColumns = computed((): DataGridColumn[] => {
    if (!this.manualValidation()) return DRAFT_COLUMNS;
    const rows = this.drafts();
    return MANUAL_DRAFT_COLUMNS.map(column => {
      if (!MANUAL_MULTI_FILTER_KEYS.has(column.key)) return column;
      return {
        ...column,
        filterType: 'multiselect' as const,
        filterOptions: column.key === 'estatus'
          ? MANUAL_VALIDATION_FILTER_OPTIONS
          : distinctDraftOptions(
              rows,
              column.key as 'emisor' | 'metodoPago' | 'formaPago' | 'usoCfdi',
            ),
      };
    });
  });
  protected readonly visibleDrafts = computed(() => {
    const rows = this.drafts();
    if (!this.manualValidation()) return rows;
    const segment = this.validationSegments().find(item => item.key === 'estatus')?.value ?? '';
    const filters = this.draftColumnFilters();
    return rows.filter(row => {
      if (segment === 'valido' && row.estatus !== 'Validado') return false;
      if (segment === 'no-valido' && row.estatus !== 'Error validación') return false;
      return Object.entries(filters).every(([key, value]) => {
        const cell = String(row[key as keyof UiInvoiceDraftRow] ?? '');
        if (MANUAL_MULTI_FILTER_KEYS.has(key)) {
          const selected = parseColumnMultiFilter(value);
          return selected.length === 0 || selected.includes(cell);
        }
        const query = value.trim().toLowerCase();
        if (!query) return true;
        return cell.toLowerCase().includes(query);
      });
    });
  });

  /** Motivo por el que el último intento de guardado no pudo completarse. */
  protected readonly saveError = signal<string | null>(null);

  constructor() {
    effect(
      () => {
        if (this.manualValidation()) return;
        const files = this.files();
        this.saveError.set(null);
        const selectionKey = files
          .filter(item => item.extension === 'xml')
          .map(item => item.id)
          .join('|');
        void this.refreshDrafts(files, selectionKey);
      },
      { allowSignalWrites: true },
    );

  }

  /** Vacía la tabla de validación. Lo usa la página al cerrar el drawer. */
  clearCaptured(): void {
    this.drafts.set([]);
    this.validatedFiles.set([]);
    this.showValidatedTable.set(false);
    this.infoOpen.set(false);
    this.criteriaTitle.set('');
    this.criteriaOrigin.set(null);
    this.criteriaRowId = null;
    this.criteriaMatch.emit(true);
    this.draftColumnFilters.set({});
    this.validationSegments.set(validationSegmentFilters());
    this.saveError.set(null);
  }

  protected async onValidate(): Promise<void> {
    const selected = this.files();
    if (selected.length === 0) {
      this.saveError.set('Agrega el archivo XML y el PDF de la factura para poder validar.');
      return;
    }

    this.validating.set(true);
    try {
      const drafts = await draftsFromInvoiceFiles(selected, { pairByFileName: true });
      if (this.exceedsValidatedSubtotal(drafts)) {
        this.saveError.set('El importe de los CFDI supera al saldo por facturar.');
        return;
      }
      this.drafts.update(rows => [...rows, ...drafts]);
      this.validatedFiles.update(rows => [...rows, ...selected]);
      this.showValidatedTable.set(true);
      this.saveError.set(null);
      this.files.set([]);
    } finally {
      this.validating.set(false);
    }
  }

  protected onDraftColumnFilters(filters: Record<string, string>): void {
    this.draftColumnFilters.set(filters);
  }

  private exceedsValidatedSubtotal(incoming: UiInvoiceDraftRow[]): boolean {
    const limit = this.maxValidatedSubtotal();
    if (limit == null) return false;
    const subtotal = (rows: UiInvoiceDraftRow[]) => rows
      .filter(row => row.estatus === 'Validado')
      .reduce((sum, row) => sum + moneyAmount(row.subtotal), 0);
    return subtotal(this.drafts()) + subtotal(incoming) > limit + 0.005;
  }

  protected onDraftSegment(filters: DataTableSegmentedFilter[]): void {
    this.validationSegments.set(filters);
  }

  protected onValidationTag(event: DataTableTagClick): void {
    if (!this.manualValidation()) return;
    const row = event.row as UiInvoiceDraftRow;
    if (event.key === 'archivo') {
      this.downloadFile(row.xml);
      return;
    }
    if (event.key === 'pdf') {
      this.downloadFile(row.pdfFile);
      return;
    }
    if (event.key !== 'estatus' || !event.origin) return;
    if (this.infoOpen() && this.criteriaRowId === row.id) {
      this.closeCriteria();
      return;
    }
    this.criteriaRowId = row.id;
    this.criteriaTitle.set(`${row.folio} - ${row.fecha} - ${row.emisor}`);
    this.criteriaMatch.emit(row.namesMatch);
    this.placeCriteriaPanel(event.origin);
    this.infoOpen.set(true);
  }

  private downloadFile(item: UiFileItem | null): void {
    if (!item) return;
    const url = URL.createObjectURL(item.file);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = item.name;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  protected closeCriteria(): void {
    this.infoOpen.set(false);
    this.criteriaRowId = null;
    this.criteriaTitle.set('');
    this.criteriaOrigin.set(null);
  }

  private placeCriteriaPanel(origin: HTMLElement): void {
    const rect = origin.getBoundingClientRect();
    const margin = 12;
    const gap = 8;
    const availableWidth = window.innerWidth - rect.right - gap - margin;
    this.panelWidth.set(Math.max(240, Math.min(600, availableWidth)));
    this.criteriaOrigin.set(origin);
    this.criteriaPositions.set([
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: gap,
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: -gap,
      },
    ]);
  }

  takeResult(): UiInvoiceCaptureResult | null {
    const files = this.manualValidation() ? this.validatedFiles() : this.files();
    const result = takeInvoiceCaptureResult(
      files,
      this.drafts(),
      this.notes(),
    );
    this.saveError.set(result ? null : this.missingFilesMessage());
    return result;
  }

  reportSaveError(message: string): void {
    this.saveError.set(message);
  }

  private missingFilesMessage(): string {
    if (this.manualValidation() && this.files().length > 0 && this.drafts().length === 0) {
      return 'Valida los archivos antes de guardar.';
    }
    const hasXml = this.drafts().length > 0;
    const hasPdf = this.files().some(item => item.extension === 'pdf');
    if (!hasXml && !hasPdf) {
      return 'Agrega el XML y el PDF de la factura para poder guardar.';
    }
    if (!hasXml) return 'Agrega el archivo XML de la factura para poder guardar.';
    return 'Agrega el archivo PDF de la factura para poder guardar.';
  }

  private async refreshDrafts(
    files: readonly UiFileItem[],
    selectionKey: string,
  ): Promise<void> {
    const drafts = await draftsFromInvoiceFiles(files);
    const currentKey = this.files()
      .filter(item => item.extension === 'xml')
      .map(item => item.id)
      .join('|');
    if (selectionKey === currentKey) this.drafts.set(drafts);
  }
}
